/**
 * Cookie consent banner logic.
 *
 * Deliberately self-contained (no dependency on editions.js or any other
 * theme script) so a bug elsewhere on the page can't break consent handling.
 *
 * Wires "accept all" / "reject non-essential" / "save preferences" into
 * Shopify's Customer Privacy API (window.Shopify.customerPrivacy) so the
 * choice actually gates Shopify Analytics, installed sales-channel pixels,
 * and any app that respects the API - not just this banner's own UI.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'maviene_cookie_consent';

  var banner = document.querySelector('[data-cookie-banner]');
  if (!banner) return;

  var mainView = banner.querySelector('[data-cookie-banner-main]');
  var prefsView = banner.querySelector('[data-cookie-banner-prefs]');
  var toggles = banner.querySelectorAll('[data-cookie-toggle]');

  function setToggleState(el, on) {
    el.setAttribute('aria-checked', on ? 'true' : 'false');
    el.classList.toggle('cookie-toggle--on', on);
  }

  function getToggleState(el) {
    return el.getAttribute('aria-checked') === 'true';
  }

  function readStoredConsent() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function storeConsent(consent) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch (e) {
      /* localStorage unavailable (e.g. private mode) - consent still gets
         sent to Shopify below, it just won't be remembered locally, so the
         banner will show again next visit. */
    }
  }

  function sendToShopify(consent, callback) {
    if (!(window.Shopify && window.Shopify.customerPrivacy)) {
      if (typeof callback === 'function') callback();
      return;
    }

    window.Shopify.customerPrivacy.setTrackingConsent(
      {
        analytics: !!consent.analytics,
        marketing: !!consent.marketing,
        preferences: true,
        sale_of_data: !!consent.marketing,
      },
      function () {
        if (typeof callback === 'function') callback();
      }
    );
  }

  function withCustomerPrivacy(callback) {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      callback();
      return;
    }
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {
        callback();
      });
      return;
    }
    callback();
  }

  function applyConsent(consent, callback) {
    storeConsent(consent);
    withCustomerPrivacy(function () {
      sendToShopify(consent, callback);
    });
  }

  function hideBanner() {
    banner.classList.remove('cookie-banner--visible');
    window.setTimeout(function () {
      banner.setAttribute('hidden', '');
    }, 400);
  }

  function showBanner() {
    banner.removeAttribute('hidden');
    // Force layout so the transition actually runs instead of the element
    // just appearing already in its "visible" state.
    // eslint-disable-next-line no-unused-expressions
    banner.offsetHeight;
    banner.classList.add('cookie-banner--visible');
  }

  function showPrefs() {
    mainView.setAttribute('hidden', '');
    prefsView.removeAttribute('hidden');
  }

  function showMain() {
    prefsView.setAttribute('hidden', '');
    mainView.removeAttribute('hidden');
  }

  function setAllToggles(on) {
    for (var i = 0; i < toggles.length; i++) {
      setToggleState(toggles[i], on);
    }
  }

  function collectToggleConsent() {
    var consent = {};
    for (var i = 0; i < toggles.length; i++) {
      consent[toggles[i].getAttribute('data-cookie-toggle')] = getToggleState(toggles[i]);
    }
    return consent;
  }

  function bindAll(selector, handler) {
    var els = banner.querySelectorAll(selector);
    for (var i = 0; i < els.length; i++) {
      els[i].addEventListener('click', handler);
    }
  }

  bindAll('[data-cookie-banner-accept]', function () {
    setAllToggles(true);
    applyConsent({ analytics: true, marketing: true }, hideBanner);
  });

  bindAll('[data-cookie-banner-reject]', function () {
    setAllToggles(false);
    applyConsent({ analytics: false, marketing: false }, hideBanner);
  });

  bindAll('[data-cookie-banner-save]', function () {
    applyConsent(collectToggleConsent(), hideBanner);
  });

  bindAll('[data-cookie-banner-manage]', showPrefs);

  var backBtn = banner.querySelector('[data-cookie-banner-back]');
  if (backBtn) backBtn.addEventListener('click', showMain);

  for (var i = 0; i < toggles.length; i++) {
    toggles[i].addEventListener('click', function (event) {
      setToggleState(event.currentTarget, !getToggleState(event.currentTarget));
    });
  }

  // Any element anywhere on the page (e.g. a footer link) can reopen the
  // preferences panel by carrying this attribute.
  var openTriggers = document.querySelectorAll('[data-cookie-banner-open]');
  for (var j = 0; j < openTriggers.length; j++) {
    openTriggers[j].addEventListener('click', function (event) {
      event.preventDefault();
      var stored = readStoredConsent();
      if (stored) {
        for (var k = 0; k < toggles.length; k++) {
          setToggleState(toggles[k], !!stored[toggles[k].getAttribute('data-cookie-toggle')]);
        }
      }
      showPrefs();
      showBanner();
    });
  }

  function init() {
    var stored = readStoredConsent();

    if (stored) {
      for (var k = 0; k < toggles.length; k++) {
        setToggleState(toggles[k], !!stored[toggles[k].getAttribute('data-cookie-toggle')]);
      }
      // Already decided locally - quietly re-sync with Shopify (in case this
      // is a new session / cookies were cleared) without showing the banner.
      withCustomerPrivacy(function () {
        sendToShopify(stored);
      });
      return;
    }

    withCustomerPrivacy(function () {
      var shouldShow = true;
      if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.shouldShowBanner === 'function') {
        try {
          shouldShow = window.Shopify.customerPrivacy.shouldShowBanner();
        } catch (e) {
          shouldShow = true;
        }
      }
      if (shouldShow) showBanner();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
